import { Box, Icon, Typography } from "@husqvarna/ui-core";
import { useMemo, useRef } from "react";
import { useIntl } from "react-intl";

import { Area } from "../../../../../models/Area";
import {UnitSystem} from "../../../../../models/Enums";
import { Product } from "../../../../../models/Product";
import { ProposalProduct } from "../../../../../models/ProposalProduct";
import { ReactComponent as YesIcon } from "../../../../../../shared/icons/yes.svg";
import { ReactComponent as NoIcon } from "../../../../../../shared/icons/no.svg";
import { getAreaSizeCoveredByOtherProducts, mowerCanHandleAreaSlope } from "../../../../../coverageCalculations";
import MowerArea from "./MowerArea/MowerArea";


const compareAreaNames = (a: Area, b: Area): number => {
  // This sorts alphabetically first, then numerically within each group
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
};

export type MowerAreaSelectorProps = {
  selectedAreas: Area[];
  savedAreas?: string[];
  onChange?: (area: Area) => void;
  isDisabled: (area: Area) => boolean;
  showCapacityError?: (area: Area) => boolean;
  showQualityError?: (area: Area) => boolean;
  showSlopeError?: (area: Area) => boolean;
  unitSystem: UnitSystem;
  mower: Product;
  proposalProduct?: ProposalProduct;
  areas: Area[];
}

const MowerAreaSelector = ({
  selectedAreas,
  savedAreas,
  onChange,
  isDisabled,
  showCapacityError,
  showQualityError,
  showSlopeError,
  unitSystem,
  mower,
  proposalProduct,
  areas
}: MowerAreaSelectorProps) => {
  const intl = useIntl();

  // Store initial values when component mounts to freeze categorization and sorting
  // Areas are already frozen from MowerDialog, but we need to snapshot for categorization logic
  const initialValuesRef = useRef<{
    savedAreas?: string[];
    initialProposalProduct?: ProposalProduct;
    areasSnapshot: Area[];
  } | null>(null);

  // Initialize ref on first render only
  if (initialValuesRef.current === null && areas.length > 0) {
    initialValuesRef.current = {
      savedAreas: savedAreas ? [...savedAreas] : undefined,
      initialProposalProduct: proposalProduct ? { ...proposalProduct } : undefined,
      areasSnapshot: areas.map(area => ({
        ...area,
        coveragePercent: area.coveragePercent,
        proposalProductConnections: area.proposalProductConnections ? [...area.proposalProductConnections] : []
      }))
    };
  }

  const areaIsSelected = (area: Area): boolean => 
    selectedAreas.some(a => a.id === area.id);

  // Categorize and sort areas into three lists
  // Use initial snapshot to freeze categorization - areas won't jump between lists while dialog is open
  // This prevents re-categorization when coveragePercentage or proposalProduct updates from database
  const { notFullyCovered, alreadyCovered, cantManageSlope } = useMemo(() => {
    const initialSnapshot = initialValuesRef.current?.areasSnapshot;
    const initialProposalProduct = initialValuesRef.current?.initialProposalProduct;
    
    if (!initialSnapshot) {
      return { notFullyCovered: [], alreadyCovered: [], cantManageSlope: [] };
    }

    const notFullyCoveredList: Area[] = [];
    const alreadyCoveredList: Area[] = [];
    const cantManageSlopeList: Area[] = [];

    // Categorize areas using initial snapshot (frozen values)
    // Use current areas for rendering (selected status may change)
    initialSnapshot.forEach((initialArea) => {
      const currentArea = areas.find(a => a.id === initialArea.id);
      if (!currentArea) return;

      // Priority 1: Can't manage slope
      if (!mowerCanHandleAreaSlope(mower, currentArea)) {
        cantManageSlopeList.push(currentArea);
        return;
      }

      // Priority 2: Already covered by other mowers
      let isFullyCovered = false;
      if (initialProposalProduct) {
        const coveredSize = getAreaSizeCoveredByOtherProducts(initialArea, initialProposalProduct);
        isFullyCovered = (coveredSize / initialArea.size) * 100 >= 100;
      } else {
        isFullyCovered = initialArea.coveragePercent >= 100;
      }

      if (isFullyCovered) {
        alreadyCoveredList.push(currentArea);
      } else {
        notFullyCoveredList.push(currentArea);
      }
    });

    // Sort list 1: saved areas (initially selected when dialog opened) first, then numerically
    // Use initial savedAreas from ref to freeze sorting - it won't change when proposalProduct updates
    const initialSavedAreas = initialValuesRef.current?.savedAreas;
    notFullyCoveredList.sort((a, b) => {
      const aSaved = initialSavedAreas && initialSavedAreas.includes(a.id);
      const bSaved = initialSavedAreas && initialSavedAreas.includes(b.id);
      if (aSaved && !bSaved) return -1;
      if (!aSaved && bSaved) return 1;
      return compareAreaNames(a, b);
    });

    // Sort list 2 and 3: numerically
    alreadyCoveredList.sort(compareAreaNames);
    cantManageSlopeList.sort(compareAreaNames);

    return {
      notFullyCovered: notFullyCoveredList,
      alreadyCovered: alreadyCoveredList,
      cantManageSlope: cantManageSlopeList
    };
  }, [mower]); // Only depend on mower - categorization is frozen based on initial snapshot

  const renderAreaList = (areaList: Area[], isCovered: boolean = false) => {
    return areaList.map((area) => {
      const disabled = isDisabled(area);
      return (
        <MowerArea
          key={area.id}
          area={area}
          showCapacityError={showCapacityError?.(area) ?? false}
          showQualityError={showQualityError?.(area) ?? false}
          showSlopeError={showSlopeError?.(area) ?? false}
          isDisabled={disabled}
          onClicked={() => !disabled && onChange?.(area)}
          isSelected={areaIsSelected(area)}
          isSaved={savedAreas?.includes(area.id) ?? false}
          isCovered={isCovered}
          unitSystem={unitSystem}
        />
      );
    });
  };

  return (
    <Box flexDirection="column" gap={3}>
      {/* List 1: Not fully covered areas */}
      {notFullyCovered.length > 0 && (
        <Box flexDirection="column" mt={1}>
          {renderAreaList(notFullyCovered)}
        </Box>
      )}

      {/* List 2: Already covered areas */}
      {alreadyCovered.length > 0 && (
        <Box flexDirection="column">
          <Box alignItems="center" gap={2} mt={2} mb={4} ml={2}>
            <Typography variant="Body2" color="Grey600" tag="h3">
              {intl.formatMessage({
                id: "project.equipment.mowerDialog.alreadyCovered",
                defaultMessage: "Already covered"
              })}
            </Typography>
            <YesIcon width={16} height={16} />
          </Box>
          {renderAreaList(alreadyCovered, true)}
        </Box>
      )}

      {/* List 3: Can't manage slope areas */}
      {cantManageSlope.length > 0 && (
        <Box flexDirection="column">
          <Box alignItems="center" gap={2} mt={2} mb={4} ml={2}>
            <Typography variant="Body2" color="Grey600" tag="h3">
              {intl.formatMessage({
                id: "project.equipment.mowerDialog.cantManageSlope",
                defaultMessage: "Can't manage slope"
              })}
            </Typography>
            <NoIcon width={16} height={16} />
          </Box>
          {renderAreaList(cantManageSlope)}
        </Box>
      )}
    </Box>
  );
};
export default MowerAreaSelector;
